import { zodResolver } from "@hookform/resolvers/zod";
import type { FC } from "react";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import { z } from "zod";

import { Button, Input, PopupWindow } from "src/components";
import { useUpdateUserProfileMutation, useUserQuery } from "src/serverApi";
import { useTranslation } from "src/translations";
import { cn, getValOrThrowError, isValidCzechoslovakBirthNumber } from "src/utils";
import { SOCIAL_SECURITY_NUMBER_HIGHLIGHT_QUERY_PARAM, SOCIAL_SECURITY_NUMBER_HIGHLIGHT_QUERY_VALUE } from "../constants";
import { logger } from "../logger";

type FormFields = {
    socialSecurityNumber: string;
};

export const SocialSecurityNumberForm: FC = () => {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();

    const { data: user } = useUserQuery();
    const [updateProfile, { isLoading }] = useUpdateUserProfileMutation();

    const existingSsn = user?.userProfile?.socialSecurityNumber;
    const isExistingSsnValid = !!existingSsn && isValidCzechoslovakBirthNumber(existingSsn);
    const isHighlighted =
        !isExistingSsnValid &&
        searchParams.get(SOCIAL_SECURITY_NUMBER_HIGHLIGHT_QUERY_PARAM) === SOCIAL_SECURITY_NUMBER_HIGHLIGHT_QUERY_VALUE;

    const schema = z.object({
        socialSecurityNumber: z
            .string()
            .min(1, t("settings.socialSecurityNumber.error"))
            .refine((value) => isValidCzechoslovakBirthNumber(value), { message: t("settings.socialSecurityNumber.errorFormat") }),
    });

    const {
        handleSubmit,
        register,
        setFocus,
        formState: { errors, isValid },
    } = useForm<FormFields>({
        mode: "onChange",
        resolver: zodResolver(schema),
        defaultValues: { socialSecurityNumber: existingSsn ?? undefined },
    });

    useEffect(() => {
        if (isHighlighted) {
            setFocus("socialSecurityNumber");
        }
    }, [isHighlighted, setFocus]);

    const onSubmit = async (data: FormFields) => {
        try {
            await updateProfile({
                profileDocumentId: getValOrThrowError(user?.userProfile.documentId).toString(),
                data: { socialSecurityNumber: data.socialSecurityNumber.replace(/[ /]/g, "") },
            }).unwrap();
            await PopupWindow.fire({
                title: t("settings.socialSecurityNumber.success.title"),
                confirmButtonText: t("settings.socialSecurityNumber.success.confirmButton"),
                icon: "success",
            });
        } catch (err) {
            logger.error("Error saving social security number", err);
            await PopupWindow.fire({
                title: t("settings.socialSecurityNumber.submitError.title"),
                text: t("settings.socialSecurityNumber.submitError.text"),
                confirmButtonText: t("settings.socialSecurityNumber.submitError.confirmButton"),
                icon: "error",
            });
        }
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)} className="w-full space-y-6">
            <Input
                label={t("settings.socialSecurityNumber.label")}
                {...register("socialSecurityNumber")}
                error={errors.socialSecurityNumber}
                disabled={isExistingSsnValid}
                className={cn(isHighlighted && "border-2 border-brand")}
            />

            {!isExistingSsnValid && (
                <Button isLoading={isLoading} type="submit" fullWidth disabled={!isValid}>
                    {t("settings.socialSecurityNumber.submit")}
                </Button>
            )}
        </form>
    );
};
